// Transfer module (โอนย้าย) — Xano contract + helpers for branch-to-branch
// stock transfers. Companion to lib/stock.js: a transfer is the ONLY thing that
// may write the balanced รับย้าย / แบ่งให้ pair into the stock ledger, and it
// does so through the 3-stage flow below rather than a hand-rolled POST /stock.
//
// Tables:
//   inventory_transfer { id, date, inventory_id, branch_id, branch2_id, N,
//                        stage, remark, created_by, modified_by,
//                        created_at, modified_at }
//   stock              — see lib/stock.js (the pair lands here on confirm)
//
// ── The three stages ───────────────────────────────────────────────────────
//   1  รออนุมัติ  — a request has been made: move stock FROM branch_id TO
//                   branch2_id. Nothing has physically moved; no stock rows yet.
//   2  รอรับของ   — approved (Manager+). Waiting for the receiving branch to
//                   confirm they actually got the goods. Still no stock rows.
//   3  เสร็จสิ้น   — receipt confirmed. The confirm step wrote the balanced pair:
//                   แบ่งให้ out of branch_id, รับย้าย into branch2_id.
//
// ── Advancing (POST /api/inventory_transfer/step) ────────────────────────────
// The `stage` you send is the stage you are advancing FROM (Xano stores +1):
//   send 1 → approve   (1→2)   Manager / Stock Manager / Admin only
//   send 2 → confirm   (2→3)   any employee; writes the stock pair
//
// ── The N-sign contract (see functions/api/inventory_transfer.js) ────────────
// inventory_transfer.N is stored NEGATIVE — the signed amount leaving the
// origin. On confirm, Xano writes origin=N (negative → แบ่งให้) and
// destination=N×-1 (positive → รับย้าย). The create proxy coerces the user's
// positive quantity to -abs(N); we send it negative from here too, and the
// display always shows the magnitude. Never surface the raw negative to a user.

export const STAGE_PENDING = 1   // รออนุมัติ
export const STAGE_SENT    = 2   // รอรับของ (approved, awaiting receipt)
export const STAGE_DONE    = 3   // เสร็จสิ้น

export const STAGES = [
  { stage: STAGE_PENDING, key: 'pending', label: 'ขอโอนย้าย', desc: 'ใบโอนที่รอผู้จัดการอนุมัติ' },
  { stage: STAGE_SENT,    key: 'sent',    label: 'รับของ',    desc: 'อนุมัติแล้ว — รอสาขาปลายทางยืนยันรับของ' },
  { stage: STAGE_DONE,    key: 'done',    label: 'เสร็จสิ้น',  desc: 'ยืนยันรับของแล้ว — ตัด/เพิ่มสต๊อกเรียบร้อย' },
]

export const stageMeta = (stage) => STAGES.find((s) => s.stage === Number(stage)) || null

// Pill tone per stage: pending = amber (needs a decision), sent = blue
// (in transit), done = emerald (settled).
export function stageTone(stage) {
  switch (Number(stage)) {
    case STAGE_PENDING: return 'bg-amber-50 text-amber-800 border-amber-200'
    case STAGE_SENT:    return 'bg-sky-50 text-sky-700 border-sky-200'
    case STAGE_DONE:    return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    default:            return 'bg-slate-50 text-slate-600 border-slate-200'
  }
}

/**
 * Build a POST /api/inventory_transfer body for ONE item, at stage 1.
 * `qty` is the positive amount to move; we store it negative (see the contract
 * note above), and the proxy re-applies -abs() so the sign can never invert.
 */
export function buildTransferPayload({ inventory_id, branch_id, branch2_id, qty, remark }) {
  return {
    inventory_id: Number(inventory_id) || 0,
    branch_id: Number(branch_id) || 0,
    branch2_id: Number(branch2_id) || 0,
    N: -Math.abs(Math.trunc(Number(qty) || 0)),
    remark: remark || '',
  }
}

/* ── read model (GET /api/inventory_transfer row) ──────────────────────────── */
//
// Each row is enriched server-side; read these, never recompute them:
//   branch_name / branch2_name / branch_color / branch2_color  (from the join)
//   inventory{}         — the full item (Name, unit, subCategory, image, …)
//   stock_branch        — CURRENT level of this item at the ORIGIN branch
//   stock_branch2       — CURRENT level of this item at the DESTINATION branch
//   _creator.code / _modified.code — who created / last touched it

// The quantity to DISPLAY — always a positive magnitude, whatever N's sign.
export const transferQty = (row) => Math.abs(Number(row?.N) || 0)

export const transferItemName = (row) =>
  String(row?.inventory?.Name || '').trim() || `#${row?.inventory_id ?? '—'}`

export const creatorCode  = (row) => row?._creator?.code || row?._creator?.[0]?.code || ''
export const modifiedCode = (row) => row?._modified?.code || row?._modified?.[0]?.code || ''

/**
 * Best-effort read of a `stock_count` addon into a number, or null when the
 * shape says nothing. The addon's exact shape isn't pinned in the docs (seen as
 * a bare number and as an object), so tolerate: number, numeric string,
 * { stock_N | N | count | total | sum }, or an array of such rows to sum.
 * Mirrors lib/inventory.js#readLevel, kept local so the two can diverge.
 */
export function stockLevel(v) {
  if (v == null) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    const s = v.trim()
    return s !== '' && Number.isFinite(Number(s)) ? Number(s) : null
  }
  if (Array.isArray(v)) {
    let sum = 0, hit = false
    for (const r of v) { const n = stockLevel(r); if (n != null) { sum += n; hit = true } }
    return hit ? sum : null
  }
  if (typeof v === 'object') {
    for (const k of ['stock_N', 'N', 'count', 'total', 'sum', 'value']) {
      if (v[k] != null) { const n = stockLevel(v[k]); if (n != null) return n }
    }
  }
  return null
}

export const originLevel = (row) => stockLevel(row?.stock_branch)
export const destLevel   = (row) => stockLevel(row?.stock_branch2)
